import React, { useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import { checkAddPermission, checkEditPermission, checkDeletePermission, handleImportClickFile, handleExportFile, handleUpdateAllCheckbox, handleUpdateDataCheckbox } from "../../../Utils/helper";
import { getApi, postApi, putApi, deleteApi } from "../../../Services/apiService";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import Import from "../../../Utils/import";

function AdminMember() {
  const navigate = useNavigate()
  const [methodType, setMethodType] = useState('');
  const { data } = useSelector((state) => state.roleData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adminMembers, setAdminMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [memberToDelete, setMemberToDelete] = useState(null);
  const [memberToEdit, setMemberToEdit] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState({
    file: '',
    general: ''
  });

  const userData = JSON.parse(localStorage.getItem('userDetail'))
  console.log("userDatauserData", userData);

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    name: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    name: ''
  });
  const [touched, setTouched] = useState({
    email: false,
    name: false
  });

  // Fetch admin members
  const fetchAdminMembers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getApi(`/admins?search=${encodeURIComponent(searchQuery)}&per_page=100`);

      if (response?.success) {
        setAdminMembers(response.data.admins?.data || []);
      } else {
        setError(response?.msg || 'Failed to fetch admin members');
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch admin members');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAdminMembers();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate immediately when typing if field was touched
    if (touched[name]) {
      validateField(name, value);
    }
  };

  // Validate single field
  const validateField = (fieldName, value) => {
    let error = '';

    if (!value.trim()) {
      error = `${fieldName} is required`;
    } else if (fieldName === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      error = 'Please enter a valid email address';
    } else if (fieldName === 'name' && value.length > 50) {
      error = 'Name should be less than 50 characters';
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return !error;
  };

  // Handle blur - mark field as touched and validate
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name, value);
  };

  // Validate entire form and mark all fields as touched
  const validateForm = () => {
    let isValid = true;

    // Mark all fields as touched to show errors
    setTouched({
      email: true,
      name: true
    });

    // Validate each field
    Object.keys(formData).forEach(key => {
      const valid = validateField(key, formData[key]);
      if (!valid) isValid = false;
    });

    return isValid;
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      email: '',
      name: ''
    });
    setErrors({
      email: '',
      name: ''
    });
    setTouched({
      email: false,
      name: false
    });
  };

  // Add new admin member
  const handleAddMember = async () => {
    // Validate form first - this will mark all fields as touched
    const isValid = validateForm();
    if (!isValid) {
      // toast.error('Please fix the validation errors');
      return;
    }

    try {
      setIsLoading(true);
      const response = await postApi('/admins', formData);

      if (response?.success) {
        toast.success(response.msg || 'Admin member added successfully');
        await fetchAdminMembers();
        setMethodType('');
        resetForm();
      } else {
        toast.error(response?.msg || 'Failed to add admin member');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to add admin member';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Set up edit form
  const setupEditForm = (member) => {
    setFormData({
      email: member.email,
      name: member.name
    });
    setMethodType('edit');
    setMemberToEdit(member);
  };

  // Update admin member
  const handleUpdateMember = async () => {
    // Validate form first - this will mark all fields as touched
    const isValid = validateForm();
    if (!isValid || !memberToEdit) {
      // toast.error('Please fix the validation errors');
      return;
    }

    try {
      setIsLoading(true);
      const response = await putApi(`/admins/${memberToEdit.id}`, formData);

      if (response?.success) {
        toast.success(response.msg || 'Admin member updated successfully');
        await fetchAdminMembers();
        setMethodType('');
        resetForm();
      } else {
        toast.error(response?.msg || 'Failed to update admin member');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to update admin member';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete confirmation
  const confirmDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      setIsLoading(true);
      const response = await deleteApi(`/admins/${memberToDelete.id}`);

      if (response?.success) {
        toast.success(response.msg || 'Admin member deleted successfully');
        await fetchAdminMembers();
      } else {
        toast.error(response?.msg || 'Failed to delete admin member');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to delete admin member';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setMemberToDelete(null);
    }
  };

  // Toggle active status
  const toggleActiveStatus = async (member) => {
    try {
      setIsLoading(true);
      const response = await putApi(`/admins/${member.id}`, {
        is_active: member.is_active ? 0 : 1
      });

      if (response?.success) {
        toast.success(response.msg || 'Status updated successfully');
        if (member.id === userData?.id) {
          localStorage.clear()
          navigate('/')
        }
        await fetchAdminMembers();
      } else {
        toast.error(response?.msg || 'Failed to update status');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to update status';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="RightSideDiv">
      {showImportModal && <Import setImportFile={setImportFile} importFile={importFile} searchQuery={searchQuery} fetchData={fetchAdminMembers} setShowImportModal={setShowImportModal} routeEnd={'/admins/import'} routeexport={'/admins/export'} type={'Admin-Member'} />}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                onClick={() => setShowDeleteModal(false)}
                className="close"
                alt="Close"
              />
            </div>
            <div className="modal-body mb-0 p-0">
              <p>Are you sure you want to delete the admin member: <strong>{memberToDelete?.name}</strong>?</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="reset" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button
                className="save mt-0"
                onClick={confirmDeleteMember}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Members List */}
      {methodType === '' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right setting-right-table">
              <div className="table-header">
                <div className="search-card my-3">
                  <img src={window.getPublicUrl('/assets/images/search-icon.svg')} alt="Search" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search Admin Member..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="table-actions">
                {checkAddPermission(data?.permissions, 'admin_member') && (
                  <button className="btn" onClick={() => handleImportClickFile(setShowImportModal, setImportFile, setImportErrors)}>
                    <img src={window.getPublicUrl('/assets/images/import.svg')} alt="Import" />
                    Import
                  </button>
                )}
                  <button className="btn" onClick={() => handleExportFile(adminMembers, searchQuery, 'adminMembers.xlsx', '/admins/export')}>
                    <img src={window.getPublicUrl('/assets/images/export.svg')} alt="Export" />
                    Export
                  </button>
                  {checkAddPermission(data?.permissions, 'admin_member') && (
                    <button className="btn add" onClick={() => setMethodType('add')}>
                      + Add Admin Member
                    </button>
                  )}
                </div>
              </div>


              <div className="permission-table-inner category-table">
                <table className="custom-table table-striped">
                  <thead>
                    <tr>
                      <th className="checkbox">
                        <label className="checbox-cs">
                          <input type="checkbox" onClick={(e) => handleUpdateAllCheckbox(e?.target?.checked, setAdminMembers)} />
                          <span className="checkmark"></span>
                        </label>
                      </th>
                      <th className="code">Sr. No.</th>
                      <th className="name">Name</th>
                      <th className="email">Email</th>
                      <th className="status">Status</th>
                      <th className="action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="6" className="text-center text-danger">{error}</td>
                      </tr>
                    ) : adminMembers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center">No admin members found</td>
                      </tr>
                    ) : (
                      adminMembers.map((member, index) => (
                        <tr key={member.id}>
                          <td className="checkbox">
                            <label className="checbox-cs">
                              <input type="checkbox" onClick={(e) => handleUpdateDataCheckbox(member, e, setAdminMembers)} checked={member?.status} />
                              <span className="checkmark"></span>
                            </label>
                          </td>
                          <td className="code">{index + 1}</td>
                          <td className="name">{member.name}</td>
                          <td className="email">{member.email}</td>
                          <td className="status">
                            <span className={`status-badge ${member.is_active ? 'active' : 'inactive'}`}>
                              {member.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="action action-cs">
                          <div class="dropdown">
                             {(checkEditPermission(data?.permissions, 'admin_member') || checkDeletePermission(data?.permissions, 'admin_member')) && (
                              <button class=" dropdown-toggle action-btn" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false">
                                Action <img src={window.getPublicUrl('/assets/images/action-drops.png')} alt="Actions" className="ps-2" />
                              </button>
                             )}
                              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                <li>{checkEditPermission(data?.permissions, 'admin_member') && (
                                  <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); setupEditForm(member); }}>
                                    Edit
                                  </a>
                                )}</li>
                                <li> {checkDeletePermission(data?.permissions, 'admin_member') && (
                                  <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); setMemberToDelete(member); setShowDeleteModal(true); }}>
                                    Delete
                                  </a>
                                )}</li>
                                <li>   {checkEditPermission(data?.permissions, 'admin_member') && (
                                  <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); toggleActiveStatus(member); }}>
                                    {member.is_active ? 'Make Inactive' : 'Make Active'}
                                  </a>
                                )}</li>
                              </ul>
                            </div>

                            {/* <div className="dropdown">
                              <button className="dropbtn action-text">
                                Actions <img src={window.getPublicUrl('/assets/images/action-drops.png')} alt="Actions" />
                              </button>
                              <div className="dropdown-content">
                                {checkEditPermission(data?.permissions, 'admin_member') && (
                                  <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); setupEditForm(member); }}>
                                    Edit
                                  </a>
                                )}
                                {checkDeletePermission(data?.permissions, 'admin_member') && (
                                  <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); setMemberToDelete(member); setShowDeleteModal(true); }}>
                                    Delete
                                  </a>
                                )}
                                {checkEditPermission(data?.permissions, 'admin_member') && (
                                  <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); toggleActiveStatus(member); }}>
                                    {member.is_active ? 'Make Inactive' : 'Make Active'}
                                  </a>
                                )}
                              </div>
                            </div> */}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>

                </table>
              </div>
              <div className="table-footer d-flex justify-content-between table-pagination">
                                {/* <select
                                  name=""
                                  id=""
                                  className="affter-change-cs page"
                                  value=''
                                  onChange=''
                                >
                                  <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="250">250</option>
                    <option value="500">500</option>
                                </select> */}
                                <div>
                                  {/* <TableFooter
                                    pageCount=''
                                    handlePageClick=''
                                    currentPage=''
                                  /> */}
                                </div>
                              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Admin Member Form */}
      {methodType === 'add' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>Add Admin Member</h3>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                className="close"
                alt="Close"
                onClick={() => {
                  setMethodType('');
                  resetForm();
                }}
              />
              <div className="setting-inner">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Email<strong>*</strong>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                        placeholder="Enter Email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {touched.email && errors.email && (
                        <div className="invalid-feedback d-block">
                          {errors.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Name<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                        placeholder="Enter Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        maxLength={50}
                      />
                      {touched.name && errors.name && (
                        <div className="invalid-feedback d-block">
                          {errors.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={resetForm}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleAddMember}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Admin Member Form */}
      {methodType === 'edit' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>Edit Admin Member</h3>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                className="close"
                alt="Close"
                onClick={() => {
                  setMethodType('');
                  resetForm();
                }}
              />
              <div className="setting-inner">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Email<strong>*</strong>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                        placeholder="Enter Email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        disabled
                      />
                      {touched.email && errors.email && (
                        <div className="invalid-feedback d-block">
                          {errors.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Name<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                        placeholder="Enter Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        maxLength={50}
                      />
                      {touched.name && errors.name && (
                        <div className="invalid-feedback d-block">
                          {errors.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setFormData({
                          email: memberToEdit?.email || '',
                          name: memberToEdit?.name || ''
                        });
                        setErrors({ email: '', name: '' });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleUpdateMember}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Updating...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default AdminMember;